import React, { useState, useEffect } from 'react';
import { StoryData } from '../../services/storyService';

interface EnhancedPrintLayoutDesignerProps {
  story: StoryData;
  onClose: () => void;
  onExportPDF: (data: any) => void;
}

const EnhancedPrintLayoutDesigner: React.FC<EnhancedPrintLayoutDesignerProps> = ({ 
  story, 
  onClose, 
  onExportPDF 
}) => {
  const [currentStep, setCurrentStep] = useState<'layout' | 'organize' | 'preview'>('layout');
  const [selectedLayout, setSelectedLayout] = useState('');
  const [layoutPages, setLayoutPages] = useState<(number | null)[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPanel, setSelectedPanel] = useState<number | null>(null);
  const [storyPages, setStoryPages] = useState<any[]>([]);

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  // Load story pages based on story type and content
  useEffect(() => {
    const extractPages = () => {
      if (!story.content) return [];
      
      switch (story.type) {
        case 'graphic-novel':
          // Extract pages from graphic novel content
          return story.content.pages || [];
        
        case 'picture-book':
          // Extract pages from picture book content
          return story.content.pages || [];
        
        case 'regular-book':
          // Extract chapters/sections from regular book
          if (story.content.chapters) {
            return story.content.chapters.map((chapter: any, index: number) => ({
              id: `chapter-${index}`,
              title: chapter.title,
              content: chapter.content,
              type: 'chapter'
            }));
          }
          return [];
        
        default:
          return [];
      }
    };

    setStoryPages(extractPages());
  }, [story]);

  // Get templates based on story type
  const getLayoutTemplatesForStoryType = () => {
    const baseTemplates = [
      {
        id: 'single-page',
        name: 'Full Page Story',
        icon: '🌟',
        description: 'One page, maximum impact',
        layout: [{ x: 50, y: 50, width: 500, height: 700 }],
        pageSize: { width: 612, height: 792 }
      }
    ];

    switch (story.type) {
      case 'graphic-novel':
        return [
          {
            id: 'comic-strip',
            name: '3-Panel Comic Strip',
            icon: '📺',
            description: 'Perfect for sequential storytelling',
            layout: [
              { x: 50, y: 100, width: 200, height: 300 },
              { x: 270, y: 100, width: 200, height: 300 },
              { x: 490, y: 100, width: 200, height: 300 }
            ],
            pageSize: { width: 792, height: 612 }
          },
          {
            id: 'comic-page',
            name: 'Comic Book Page',
            icon: '📖',
            description: 'Professional comic layout',
            layout: [
              { x: 50, y: 50, width: 300, height: 200 },
              { x: 370, y: 50, width: 300, height: 200 },
              { x: 50, y: 270, width: 620, height: 150 },
              { x: 50, y: 440, width: 200, height: 150 },
              { x: 270, y: 440, width: 200, height: 150 },
              { x: 490, y: 440, width: 180, height: 150 }
            ],
            pageSize: { width: 612, height: 792 }
          },
          ...baseTemplates
        ];

      case 'picture-book':
        return [
          {
            id: 'picture-showcase',
            name: 'Picture Showcase',
            icon: '🎨',
            description: 'Large images with text below',
            layout: [
              { x: 50, y: 50, width: 500, height: 400 },
              { x: 50, y: 470, width: 500, height: 100 }
            ],
            pageSize: { width: 612, height: 792 }
          },
          {
            id: 'picture-grid',
            name: 'Picture Grid',
            icon: '🖼️',
            description: 'Multiple pictures in grid layout',
            layout: [
              { x: 50, y: 50, width: 250, height: 200 },
              { x: 320, y: 50, width: 250, height: 200 },
              { x: 50, y: 270, width: 250, height: 200 },
              { x: 320, y: 270, width: 250, height: 200 },
              { x: 50, y: 490, width: 520, height: 100 }
            ],
            pageSize: { width: 612, height: 792 }
          },
          ...baseTemplates
        ];

      case 'regular-book':
        return [
          {
            id: 'chapter-summary',
            name: 'Chapter Summary',
            icon: '📚',
            description: 'Highlight key chapters',
            layout: [
              { x: 50, y: 50, width: 520, height: 150 },
              { x: 50, y: 220, width: 520, height: 150 },
              { x: 50, y: 390, width: 520, height: 150 },
              { x: 50, y: 560, width: 520, height: 150 }
            ],
            pageSize: { width: 612, height: 792 }
          },
          {
            id: 'text-layout',
            name: 'Text Layout',
            icon: '📄',
            description: 'Clean text presentation',
            layout: [
              { x: 50, y: 50, width: 520, height: 700 }
            ],
            pageSize: { width: 612, height: 792 }
          },
          ...baseTemplates
        ];

      default:
        return baseTemplates;
    }
  };

  const layoutTemplates = getLayoutTemplatesForStoryType();
  const currentTemplate = layoutTemplates.find(t => t.id === selectedLayout);

  const assignPageToPanel = (panelIndex: number, pageIndex: number) => {
    const newLayoutPages = [...layoutPages];
    
    // Remove the page from any other panel it might be assigned to
    const existingPanelIndex = newLayoutPages.indexOf(pageIndex);
    if (existingPanelIndex !== -1) {
      newLayoutPages[existingPanelIndex] = null;
    }
    
    // Assign the page to the new panel
    newLayoutPages[panelIndex] = pageIndex;
    setLayoutPages(newLayoutPages);
    setSelectedPanel(null);
  };

  const removePageFromPanel = (panelIndex: number) => {
    const newLayoutPages = [...layoutPages];
    newLayoutPages[panelIndex] = null;
    setLayoutPages(newLayoutPages);
  };

  const fillSequentially = (startPage: number = 0) => {
    if (!currentTemplate) return;
    const newLayoutPages = [];
    for (let i = 0; i < currentTemplate.layout.length; i++) {
      if (startPage + i < storyPages.length) {
        newLayoutPages[i] = startPage + i;
      } else {
        newLayoutPages[i] = null;
      }
    }
    setLayoutPages(newLayoutPages);
    setSelectedPanel(null);
  };

  const clearLayout = () => {
    setLayoutPages([]);
    setSelectedPanel(null);
  };

  const exportToPDF = () => {
    onExportPDF({
      story: story,
      layout: selectedLayout,
      pages: layoutPages,
      template: currentTemplate,
      storyPages: storyPages
    });
  };

  const filteredPages = storyPages.filter((page, index) => 
    searchTerm === '' || 
    `Page ${index + 1}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (page.title && page.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stepTitles = {
    layout: 'Choose Layout Template',
    organize: 'Organize Your Pages',
    preview: 'Preview & Export'
  };

  const renderStepIndicator = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      padding: '20px',
      background: '#f8f9fa',
      borderBottom: '2px solid #e9ecef'
    }}>
      {(['layout', 'organize', 'preview'] as const).map((step, index) => (
        <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 20px',
            borderRadius: '25px',
            background: currentStep === step ? theme.primary : '#e9ecef',
            color: currentStep === step ? 'white' : '#666',
            fontSize: '14px',
            fontWeight: '700',
            transition: 'all 0.3s ease'
          }}>
            <span style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: currentStep === step ? 'white' : '#ccc',
              color: currentStep === step ? theme.primary : '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '900'
            }}>
              {index + 1}
            </span>
            <span>{stepTitles[step]}</span>
          </div>
          {index < 2 && (
            <div style={{
              width: '30px',
              height: '2px',
              background: index < Object.keys(stepTitles).indexOf(currentStep) ? theme.primary : '#e9ecef'
            }} />
          )}
        </div>
      ))}
    </div>
  );

  const renderLayoutSelection = () => (
    <div style={{
      padding: '40px',
      maxWidth: '1000px',
      margin: '0 auto'
    }}>
      <div style={{
        textAlign: 'center',
        marginBottom: '40px'
      }}>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: '900',
          margin: '0 0 10px 0',
          background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Choose Layout for "{story.title}"
        </h2>
        <p style={{
          fontSize: '1.1rem',
          color: '#666',
          margin: 0
        }}>
          Select the perfect layout for your {story.type.replace('-', ' ')} ({storyPages.length} pages available)
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '30px',
        marginBottom: '40px'
      }}>
        {layoutTemplates.map(template => (
          <div
            key={template.id}
            onClick={() => setSelectedLayout(template.id)}
            style={{
              padding: '30px',
              borderRadius: '20px',
              border: `4px solid ${selectedLayout === template.id ? theme.primary : '#e9ecef'}`,
              background: selectedLayout === template.id ? `${theme.primary}05` : 'white',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxShadow: selectedLayout === template.id 
                ? `0 10px 30px ${theme.primary}20` 
                : '0 5px 20px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{
              fontSize: '48px',
              marginBottom: '20px'
            }}>
              {template.icon}
            </div>
            <h3 style={{
              fontSize: '1.4rem',
              fontWeight: '800',
              margin: '0 0 10px 0',
              color: selectedLayout === template.id ? theme.primary : '#333'
            }}>
              {template.name}
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#666',
              margin: '0 0 15px 0',
              lineHeight: '1.5'
            }}>
              {template.description}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              fontSize: '14px',
              fontWeight: '600',
              color: theme.accent
            }}>
              <span>{template.layout.length} panels</span>
              <span>{template.pageSize.width}×{template.pageSize.height}px</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={onClose}
          style={{
            background: '#f8f9fa',
            color: '#666',
            border: '2px solid #e9ecef',
            borderRadius: '12px',
            padding: '12px 24px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '700'
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            setCurrentStep('organize');
            setLayoutPages(new Array(currentTemplate?.layout.length || 0).fill(null));
          }}
          disabled={!selectedLayout}
          style={{
            background: selectedLayout ? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` : '#e9ecef',
            color: selectedLayout ? 'white' : '#ccc',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            cursor: selectedLayout ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '700'
          }}
        >
          Next: Organize Pages →
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        width: '95%',
        maxWidth: '1400px',
        height: '95%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
          color: 'white',
          padding: '20px 30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '1.8rem',
              fontWeight: '900'
            }}>
              Print Layout Designer
            </h1>
            <p style={{
              margin: '5px 0 0 0',
              opacity: 0.9,
              fontSize: '1rem'
            }}>
              Create print layouts for "{story.title}"
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 15px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '700'
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Main Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {currentStep === 'layout' && renderLayoutSelection()}
          {/* Add organize and preview steps here - similar to the multi-step designer */}
        </div>

        {/* Footer Navigation */}
        {currentStep !== 'layout' && (
          <div style={{
            padding: '20px 30px',
            background: '#f8f9fa',
            borderTop: '2px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={() => {
                if (currentStep === 'organize') setCurrentStep('layout');
                if (currentStep === 'preview') setCurrentStep('organize');
              }}
              style={{
                background: 'white',
                color: '#666',
                border: '2px solid #e9ecef',
                borderRadius: '12px',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700'
              }}
            >
              ← Back
            </button>

            <button
              onClick={exportToPDF}
              disabled={layoutPages.filter((p: number | null) => p !== null).length === 0}
              style={{
                background: layoutPages.filter((p: number | null) => p !== null).length > 0 
                  ? `linear-gradient(135deg, ${theme.success}, ${theme.accent})` 
                  : '#e9ecef',
                color: layoutPages.filter((p: number | null) => p !== null).length > 0 ? 'white' : '#ccc',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                cursor: layoutPages.filter((p: number | null) => p !== null).length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '700'
              }}
            >
              Export to PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedPrintLayoutDesigner;